import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperCore } from "swiper";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/thumbs";


import { FreeMode, Thumbs } from "swiper/modules";

import styles from "./ImageSlider.module.scss";
import img1 from "./../images/black-1.jpg";
import img2 from "./../images/black-2.jpg";
import img3 from "./../images/black-3.jpg";
import img4 from "./../images/black-4.jpg";
import { IProduct } from "../../../../models/IProduct";
import { API_BASE_URL } from "../../../../config/api";

interface ImageSliderProps {
  product: IProduct;
}

type SlideItem = {
  id: number;
  url: string;
  isVideo: boolean;
};

const isVideoUrl = (url: string) => /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
const KNOWN_VIDEO_PUBLIC_LINKS = new Set([
  "https://disk.yandex.ru/i/hxim_9qT8zid_w",
  "https://disk.yandex.ru/i/qaF46T9BapZMfA",
]);
const isLikelyVideo = (rawUrl: string) => {
  if (isVideoUrl(rawUrl)) return true;
  const lower = rawUrl.toLowerCase();
  if (KNOWN_VIDEO_PUBLIC_LINKS.has(rawUrl)) return true;
  return lower.includes("vid1") || lower.includes("vid2");
};
const toMediaSrc = (url: string) =>
  /^https?:\/\//.test(url) ? url : `${API_BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;

const ImageSlider: React.FC<ImageSliderProps> = ({ product }) => {
  const [thumbsSwiper, setThumbsSwiper] = React.useState<SwiperCore | null>(null);
  const [mainSwiper, setMainSwiper] = React.useState<SwiperCore | null>(null);
  const [isMobileLayout, setIsMobileLayout] = React.useState(false);
  const [videoPlaying, setVideoPlaying] = React.useState<Record<number, boolean>>({});
  const videoRefs = React.useRef<Record<number, HTMLVideoElement | null>>({});
  const slides = React.useMemo<SlideItem[]>(() => {
    if (!product.media_gallery && !product.image_gallery) {
      return [img1, img2, img3, img4].map((url, id) => ({ id, url, isVideo: false }));
    }

    try {
      const source = product.media_gallery || product.image_gallery || "[]";
      const parsed = JSON.parse(source) as string[];
      if (!Array.isArray(parsed) || parsed.length === 0) {
        return [img1, img2, img3, img4].map((url, id) => ({ id, url, isVideo: false }));
      }
      return parsed.map((url, id) => ({ id, url: toMediaSrc(url), isVideo: isLikelyVideo(url) }));
    } catch (error) {
      console.error("Некорректный формат media_gallery:", error);
      return [img1, img2, img3, img4].map((url, id) => ({ id, url, isVideo: false }));
    }
  }, [product.media_gallery, product.image_gallery]);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobileLayout(window.innerWidth <= 992);
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    if (!mainSwiper) return;

    const syncActiveVideo = () => {
      const activeIndex = mainSwiper.realIndex;
      Object.entries(videoRefs.current).forEach(([key, el]) => {
        if (!el) return;
        const slideId = Number(key);

        el.muted = true;
        el.defaultMuted = true;
        el.volume = 0;
        if (slideId !== activeIndex) {
          el.pause();
          return;
        }
        const playPromise = el.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {

          });
        }
      });
    };

    mainSwiper.on("slideChange", syncActiveVideo);
    syncActiveVideo();

    return () => {
      mainSwiper.off("slideChange", syncActiveVideo);
    };
  }, [mainSwiper, slides]);

  return (
    <div className={styles.sliderLayoutContainer}>
      <div className={styles.thumbsVerticalAlignWrapper}>
        <Swiper
          onSwiper={setThumbsSwiper}
          direction={isMobileLayout ? "horizontal" : "vertical"}
          spaceBetween={isMobileLayout ? 10 : 33}
          slidesPerView={isMobileLayout ? "auto" : 4}
          freeMode={true}
          watchSlidesProgress={true}
          modules={[FreeMode, Thumbs]}
          className={styles.mySwiperThumbs}
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.id} className={styles.thumbSlide}>
              {slide.isVideo ? (
                <video src={slide.url} muted playsInline preload="metadata" />
              ) : (
                <img src={slide.url} alt={`Thumbnail ${slide.id}`} />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <Swiper
        onSwiper={setMainSwiper}
        thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
        modules={[Thumbs]}
        className={styles.mySwiperMain}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id} className={styles.mainSlide}>
            {slide.isVideo ? (
              <div className={styles.videoWrap}>
                <video
                  ref={(el) => {
                    videoRefs.current[slide.id] = el;
                  }}
                  src={slide.url}
                  playsInline
                  preload="metadata"
                  muted
                  controls={false}
                  className={styles.mainVideo}
                  onPlay={() => setVideoPlaying((s) => ({ ...s, [slide.id]: true }))}
                  onPause={() => setVideoPlaying((s) => ({ ...s, [slide.id]: false }))}
                  onClick={() => {
                    const el = videoRefs.current[slide.id];
                    if (!el) return;
                    if (el.paused) {
                      void el.play();
                    } else {
                      el.pause();
                    }
                  }}
                />
                <div className={styles.videoControls}>
                  <button
                    type="button"
                    className={styles.videoPlayBtn}
                    aria-label={videoPlaying[slide.id] ? "Пауза" : "Воспроизведение"}
                    onClick={(e) => {
                      e.stopPropagation();
                      const el = videoRefs.current[slide.id];
                      if (!el) return;
                      if (el.paused) {
                        void el.play();
                      } else {
                        el.pause();
                      }
                    }}
                  >
                    {videoPlaying[slide.id] ? "Пауза" : "Воспроизведение"}
                  </button>
                </div>
              </div>
            ) : (
              <img src={slide.url} alt={`Slide ${slide.id}`} />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default ImageSlider;
